import { z } from "zod";
/** Calendar month without an implicit timezone or day. */
export const CalendarMonthSchema = z
    .string()
    .regex(/^\d{4}-(?:0[1-9]|1[0-2])$/, "Expected a calendar month in YYYY-MM form")
    .brand();
/**
 * Exact YYYY-MM-DD or legacy YYYY-MM contract/employment date (D5). Month-only
 * values are accepted for backward compatibility with pre-022 data.
 *
 * Rejects impossible calendar dates: the month must be 01-12 and, when a day is
 * present, the day must be a real calendar day (e.g. `2026-02-31` and
 * `2026-02-29` in a non-leap year fail, while `2024-02-29` passes). The day is
 * validated by round-tripping through `Date.UTC` so the gregorian calendar
 * rules apply.
 */
export const PayrollDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}(-\d{2})?$/, "Expected YYYY-MM or YYYY-MM-DD")
    .refine((value) => {
    const [year, month, day] = value.split("-");
    const monthNumber = Number(month);
    if (monthNumber < 1 || monthNumber > 12)
        return false;
    if (!day)
        return true; // month-only form: any 01-12 is a valid month
    // Day-bearing form: round-trip through Date.UTC to reject impossible dates.
    const date = new Date(Date.UTC(Number(year), monthNumber - 1, Number(day)));
    return (!Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value);
}, "Expected a real calendar date in YYYY-MM-DD form")
    .brand();
/** RFC 3339 timestamp normalized to UTC (`Z`). */
export const UtcTimestampSchema = z
    .string()
    .datetime({ offset: false })
    .brand();
const EXACT_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Return the `YYYY-MM` calendar-month prefix of an exact `YYYY-MM-DD` date
 * (e.g. `"2026-05-24"` -> `"2026-05"`). A value that is already `YYYY-MM`
 * (month-only) is passed through unchanged.
 *
 * Replaces scattered `.slice(0, 7)` calls. Inputs must be well-formed
 * `YYYY-MM` or `YYYY-MM-DD` (the date schemas/domain validate before this
 * runs); behavior for other shapes is undefined.
 */
export const calendarMonthOf = (date) => EXACT_DAY_RE.test(date) ? date.slice(0, 7) : date;
/**
 * True when the interval `[from, through]` covers any day of `month` at day
 * precision.
 *
 * Semantics (shared by the API domain and the web app — the single source of
 * truth for exact-day period coverage):
 * - Exact `from` and `through` dates are inclusive: a period that
 *   ends on the last day of a month still covers that month (its closing month
 *   is included), and the following month is not covered.
 * - `from` and `through` may be exact `YYYY-MM-DD` or month-only `YYYY-MM`.
 *   Month-only `from` maps to the first day of its month (`<month>-01`);
 *   month-only `through` maps to the first instant of the FOLLOWING month
 *   (exclusive end), so a month-only closing month stays included — the same
 *   policy as `parseStartDate`/`parseBoundaryDate` in the API domain.
 * - An empty `through` (or `undefined`/`null`) means open-ended: the interval
 *   has no upper bound and covers every month from `from` onward.
 * - `month` is always a `YYYY-MM` calendar month.
 *
 * Comparison is purely lexicographic; inputs are expected to be validated,
 * well-formed dates only (the schema/domain validates before this runs).
 */
export const periodCoversMonth = (from, through, month) => {
    if (!from)
        return false;
    const fromDay = calendarMonthOf(from) === from ? `${from}-01` : from;
    const monthFrom = `${month}-01`;
    const monthAfter = monthAfterMonth(month);
    const monthTo = `${monthAfter}-01`;
    // The interval must start before the month ends, and the month must start
    // before the interval ends (open-ended through = no upper bound).
    if (!(fromDay < monthTo))
        return false;
    if (!through)
        return true;
    const throughDay = calendarMonthOf(through) === through
        ? `${monthAfterMonth(through)}-01`
        : through;
    return calendarMonthOf(through) === through
        ? throughDay > monthFrom
        : throughDay >= monthFrom;
};
/** The `YYYY-MM` month immediately following `month` (12 -> next January). */
const monthAfterMonth = (month) => {
    const year = Number(month.slice(0, 4));
    const monthNumber = Number(month.slice(5, 7));
    return monthNumber === 12
        ? `${year + 1}-01`
        : `${month.slice(0, 5)}${String(monthNumber + 1).padStart(2, "0")}`;
};
//# sourceMappingURL=time.js.map